import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { t } from 'i18next';
import { Box } from '@mui/material';
import { SideNavItem } from '@atoms';
import { ScreenI } from './index';
import { sideNavClickEvent } from './events';
import styles from './styles';

interface RenderNavItemProps {
  path?: string;
  disabled?: boolean;
  external?: boolean;
  icon?: React.ReactElement;
  title?: string;
  subMenus?: ScreenI[];
  isNew?: boolean;
  pathname?: string;
  shouldDisplayItem: (item: ScreenI) => boolean;
  validateRenderNavItem?: () => boolean | undefined;
}

/**
 * Renders a navigation item and its sub-items if any.
 * @param props - Props object.
 * @param props.path - The path of the navigation item.
 * @param props.disabled - Whether the navigation item is disabled.
 * @param props.external - Whether the navigation item is external.
 * @param props.icon - The icon of the navigation item.
 * @param props.title - The title of the navigation item.
 * @param props.subMenus - An array of sub-navigation items.
 * @param props.isNew - Whether the navigation item is new.
 * @returns - React component representing the navigation item.
 */
const RenderNavItem = ({
  path,
  disabled,
  icon,
  title = '',
  subMenus,
  isNew,
  shouldDisplayItem,
  validateRenderNavItem,
}: RenderNavItemProps) => {
  const pathname = useLocation().pathname;
  const active = path ? pathname === path : false;
  const subItemActive = subMenus ? subMenus.some((subItem) => subItem.path === pathname) : false;
  const [isOpen, setIsOpen] = useState(subItemActive);

  /** Open and close the submenu */
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <SideNavItem
        active={active || subItemActive}
        disabled={disabled}
        icon={icon}
        path={path}
        title={title}
        hasSubMenus={!!subMenus}
        isNew={isNew}
        onClick={() => {
          sideNavClickEvent(t(title));
          handleToggle();
        }}
        isOpen={isOpen}
        validateRenderNavItem={validateRenderNavItem}
      />
      {subMenus && isOpen && (
        <Box component='nav' sx={styles.subNavContainer}>
          <Box component='ul' sx={styles.subStackContainer(true)}>
            {subMenus.filter(shouldDisplayItem).map((subItem) => {
              return (
                <SideNavItem
                  key={subItem.title}
                  active={pathname === subItem.path}
                  disabled={subItem.disabled}
                  icon={subItem.icon}
                  path={subItem.path}
                  title={subItem.title ?? ''}
                  isNew={subItem.isNew}
                  isSubItem
                  onClick={() => sideNavClickEvent(`${t(title)}_${t(subItem?.title ?? '')}`)}
                  validateRenderNavItem={subItem.validateShowMenu}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default RenderNavItem;
